import { Fragment, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import { useUserRole } from '@/hooks/useUserRole'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useYardiGuestSearch } from '../hooks/useYardiGuestSearch'
import { useYardiGuestInvite } from '../hooks/useYardiGuestInvite'
import {
  YARDI_GUEST_SEARCH_COL_WIDTHS_PCT,
  YARDI_GUEST_SEARCH_CRITERIA_MESSAGE,
  YARDI_STATUS_MAPPING,
} from '../constants'
import type { YardiGuestCardApplication, FailedReasonCellProps, GridRowProps } from '../types'
import {
  formatLrCheckErrorForDisplay,
  sanitizeDataTestIdSegment,
} from '../utils/yardiInviteUi'
import {
  canInviteGuestCardApplication,
  canRetryGuestCardApplication,
  getGuestCardPrimaryApplicant,
  guestCardApplicationExpandKey,
} from '../utils/yardiGuestCardGrouping'
import { cn } from '@/lib/utils/helpers'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import yardiTableStyles from '../styles/yardiListing.module.css'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ValidationModal } from '@/components/ui/ValidationModal'
import { ConfirmShellModal } from '@/components/ui/ConfirmShellModal'
import {
  TableSearchFilterEmptyPanel,
} from '@/components/ui/TableSearchFilterEmptyState'
import { YardiGuestSearchForm } from '../components/YardiGuestSearchForm'
import { YardiGuestInviteModal } from '../components/YardiGuestInviteModal'
import { YardiGuestCardOccupantsPanel } from '../components/YardiGuestCardOccupantsPanel'

function formatShortDate(v: string | null | undefined): string {
  if (!v) return ''
  try {
    const d = parseISO(String(v).replace(' ', 'T'))
    if (isValid(d)) return format(d, 'MM/dd/yyyy')
    const d2 = new Date(v)
    if (isValid(d2)) return format(d2, 'MM/dd/yyyy')
  } catch {
    /* ignore */
  }
  return String(v)
}

function applicationCreditCheckStatus(application: YardiGuestCardApplication): string {
  const primary = getGuestCardPrimaryApplicant(application.applicants)
  return primary.creditCheckStatus ?? application.applicants[0]?.creditCheckStatus ?? ''
}

function FailedReasonCell({
  application,
  isLastRow,
  rowKey,
}: FailedReasonCellProps) {
  if (application.state !== 'FAILED' || !application.lr_check_error) return null

  return (
    <span
      className="inline-flex items-center gap-1 align-middle"
      data-testid={`yardi-guest-search-failure-reason-cell-${rowKey}`}
    >
      <InfoPopover
        content={formatLrCheckErrorForDisplay(application.lr_check_error)}
        align="end"
        maxWidth={320}
        isLastRow={isLastRow}
        testId={`yardi-guest-search-failure-details-popover-${rowKey}`}
        className="text-slate-800"
      >
        <button
          type="button"
          data-testid={`yardi-guest-search-failure-details-button-${rowKey}`}
          className={cn(
            'inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0.5 align-middle',
            'text-sm leading-none text-green-600 hover:text-green-700',
            'focus-visible:rounded-sm focus-visible:outline focus-visible:ring-2 focus-visible:ring-green-600/45 focus-visible:ring-offset-1',
          )}
          aria-label="Show application failure details"
          title="Show failure details"
        >
          <i className="fa fa-info-circle text-green-600" aria-hidden />
        </button>
      </InfoPopover>
    </span>
  )
}

export function YardiGuestSearchPage() {
  const role = useUserRole()
  const isMobile = useIsMobile()
  const isListView = !isMobile

  const guest = useYardiGuestSearch({
    orgId: role.orgId != null ? String(role.orgId) : null,
    companyId: role.companyId != null ? String(role.companyId) : null,
    propertyId: role.propertyId != null ? String(role.propertyId) : null,
    orgName: role.orgName != null ? String(role.orgName) : null,
    companyName: role.companyName != null ? String(role.companyName) : null,
    propertyName: role.propertyName != null ? String(role.propertyName) : null,
  })

  const [expandedApplicationKeys, setExpandedApplicationKeys] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!guest.applications?.length) {
      setExpandedApplicationKeys(new Set())
      return
    }
    setExpandedApplicationKeys(
      new Set(guest.applications.map((application, idx) => guestCardApplicationExpandKey(application, idx))),
    )
  }, [guest.applications])

  const guestInvite = useYardiGuestInvite({
    companyId: guest.form.watch('company_id'),
    propertyId: guest.form.watch('property_id'),
    onInviteSent: guest.markGuestCardsInviteSent,
  })

  const toggleApplication = (key: string) => {
    setExpandedApplicationKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const renderApplicationSummary = (application: YardiGuestCardApplication, idx: number) => {
    const expandKey = guestCardApplicationExpandKey(application, idx)
    const rowSuffix = sanitizeDataTestIdSegment(expandKey)

    return { expandKey, rowSuffix }
  }

  return (
    <div
      className="min-h-screen bg-[#f8fafc] p-0 pb-12 font-sans text-slate-900"
      data-testid="yardi-guest-search-page"
    >
      <PageHeader title="Search/ Invite Yardi guest cards" testIdRoot="yardi-guest-search-page-header" />

      <ConfirmShellModal
        isOpen={guest.retryErrorModal != null}
        title={guest.retryErrorModal?.title ?? 'Error'}
        bodyHtml={guest.retryErrorModal?.bodyHtml ?? ''}
        onClose={guest.clearRetryErrorModal}
        overlayDataTestId="yardi-guest-search-retry-error-modal-overlay"
        panelDataTestId="yardi-guest-search-retry-error-modal-panel"
        closeButtonDataTestId="yardi-guest-search-retry-error-modal-close"
        okButtonDataTestId="yardi-guest-search-retry-error-modal-ok"
      />

      <div
        className="mx-auto flex w-full min-w-0 flex-col gap-3 px-3 py-4 sm:gap-4 sm:px-6 sm:py-6"
        data-testid="yardi-guest-search-main"
      >
        <YardiGuestSearchForm guest={guest} />

        <ValidationModal
          isOpen={guest.guestSearchCriteriaModalOpen}
          title="Validation Message"
          message={YARDI_GUEST_SEARCH_CRITERIA_MESSAGE}
          alignMessage
          showValidationIcon
          onClose={guest.dismissGuestSearchCriteriaModal}
          overlayDataTestId="yardi-guest-search-criteria-validation-modal"
          closeButtonDataTestId="yardi-guest-search-criteria-validation-close"
          confirmButtonDataTestId="yardi-guest-search-criteria-validation-ok"
        />

        <YardiGuestInviteModal
          isOpen={guestInvite.modalOpen}
          occupants={guestInvite.occupants}
          selectedProspectIds={guestInvite.selectedProspectIds}
          sending={guestInvite.sending}
          sendPhase={guestInvite.sendPhase}
          validating={guestInvite.validating}
          canSend={guestInvite.canSend}
          validationResult={guestInvite.validationResult}
          onClose={guestInvite.closeInviteModal}
          onToggleOccupant={guestInvite.toggleOccupant}
          onRevalidate={guestInvite.revalidate}
          onSendInvite={guestInvite.sendInvite}
        />

        {guest.resultsLoading ? (
          <div
            data-testid="yardi-guest-search-loading"
            className="flex min-h-[280px] items-center justify-center rounded-xl border border-slate-200 bg-white py-20 shadow-sm"
          >
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading" />
          </div>
        ) : guest.applications && guest.applications.length > 0 ? (
          isListView ? (
            <div
              data-testid="yardi-guest-search-results-card"
              className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              <div className={yardiTableStyles.tableCard} data-testid="yardi-guest-search-table-card">
                <div
                  className={`${yardiTableStyles.tableResponsiveScroll} touch-scroll`}
                  data-testid="yardi-guest-search-table-scroll"
                >
                  <table
                    className={cn(
                      yardiTableStyles.listingTable,
                      yardiTableStyles.yardiListingTable,
                      yardiTableStyles.yardiInviteListTable,
                      yardiTableStyles.yardiGuestSearchTable,
                      'table-fixed w-full min-w-[960px] max-w-none [&>thead>tr>th]:!whitespace-normal [&>tbody>tr>td:not([colspan])]:!whitespace-normal',
                      '[&>thead>tr>th:not(:last-child)]:break-words [&>tbody>tr>td:not([colspan])]:break-words',
                      '[&>tbody>tr>td:not([colspan])]:!px-1.5 [&>tbody>tr>td:not([colspan])]:!py-2 [&>tbody>tr>td:not([colspan])]:!text-[12px] [&>tbody>tr>td:not([colspan])]:!font-normal [&>tbody>tr>td:not([colspan])]:!leading-snug',
                    )}
                    data-testid="yardi-guest-search-table"
                  >
                    <colgroup>
                      {YARDI_GUEST_SEARCH_COL_WIDTHS_PCT.map((pct, i) => (
                        <col key={i} style={{ width: `${pct}%` }} />
                      ))}
                    </colgroup>
                    <thead data-testid="yardi-guest-search-table-head">
                      <tr>
                        <th
                          className={yardiTableStyles.guestSearchExpandCell}
                          data-testid="yardi-guest-search-table-col-expand"
                          aria-label="Expand"
                        />
                        <th data-testid="yardi-guest-search-table-col-lease-start">Lease Start Date</th>
                        <th data-testid="yardi-guest-search-table-col-target-move-in">Target Move In Date</th>
                        <th data-testid="yardi-guest-search-table-col-expected-move-in">Expected Move In Date</th>
                        <th data-testid="yardi-guest-search-table-col-credit-check">Credit Check Status</th>
                        <th data-testid="yardi-guest-search-table-col-unit-number">Unit Number</th>
                        <th data-testid="yardi-guest-search-table-col-lease-duration">Lease Duration</th>
                        <th data-testid="yardi-guest-search-table-col-status">Status</th>
                        <th data-testid="yardi-guest-search-table-col-action">Action</th>
                      </tr>
                    </thead>
                    <tbody data-testid="yardi-guest-search-table-body">
                      {guest.applications.map((application, idx) => {
                        const isLast = idx === guest.applications!.length - 1
                        const { expandKey, rowSuffix } = renderApplicationSummary(application, idx)
                        const isExpanded = expandedApplicationKeys.has(expandKey)

                        return (
                          <Fragment key={expandKey}>
                            <tr data-testid={`yardi-guest-search-table-row-${rowSuffix}`}>
                              <td
                                className={yardiTableStyles.guestSearchExpandCell}
                                data-testid={`yardi-guest-search-table-row-expand-${rowSuffix}`}
                              >
                                <button
                                  type="button"
                                  data-testid={`yardi-guest-search-expand-applicants-button-${rowSuffix}`}
                                  className={cn(
                                    'inline-flex h-6 w-6 items-center justify-center rounded border-0 bg-transparent p-0 text-slate-700',
                                    'hover:bg-slate-100/90 hover:text-slate-900',
                                    'focus-visible:outline focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40 focus-visible:ring-offset-1',
                                  )}
                                  onClick={() => toggleApplication(expandKey)}
                                  aria-label="Toggle applicants"
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" aria-hidden />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" aria-hidden />
                                  )}
                                </button>
                              </td>
                              <td data-testid={`yardi-guest-search-cell-lease-start-${rowSuffix}`}>
                                {formatShortDate(application.leaseStartDate)}
                              </td>
                              <td data-testid={`yardi-guest-search-cell-target-move-in-${rowSuffix}`}>
                                {formatShortDate(application.targetMoveInDate)}
                              </td>
                              <td data-testid={`yardi-guest-search-cell-expected-move-in-${rowSuffix}`}>
                                {formatShortDate(application.expectedMoveInDate)}
                              </td>
                              <td data-testid={`yardi-guest-search-cell-credit-check-${rowSuffix}`}>
                                {applicationCreditCheckStatus(application)}
                              </td>
                              <td data-testid={`yardi-guest-search-cell-unit-number-${rowSuffix}`}>
                                {application.unitNumber}
                              </td>
                              <td data-testid={`yardi-guest-search-cell-lease-duration-${rowSuffix}`}>
                                {application.leaseDuration ?? ''}
                              </td>
                              <td
                                className="min-w-0"
                                data-testid={`yardi-guest-search-cell-status-${rowSuffix}`}
                              >
                                {application.state && (
                                  <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-1 break-words">
                                    {YARDI_STATUS_MAPPING[application.state] || application.state}
                                    <FailedReasonCell
                                      application={application}
                                      isLastRow={isLast}
                                      rowKey={rowSuffix}
                                    />
                                  </span>
                                )}
                              </td>
                              <td data-testid={`yardi-guest-search-cell-action-${rowSuffix}`}>
                                {canRetryGuestCardApplication(application) ? (
                                  <button
                                    type="button"
                                    data-testid={`yardi-guest-search-retry-button-${rowSuffix}`}
                                    disabled={guest.retryingApplicationId != null}
                                    className={cn(
                                      'relative inline-flex items-center justify-center rounded-lg border-0 bg-[rgb(21_151_255)] px-3 py-1.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all',
                                      'hover:bg-[#0d7acc] hover:shadow-xl',
                                      'disabled:pointer-events-none disabled:opacity-50',
                                    )}
                                    onClick={() =>
                                      void guest.retryapplication(application.yardi_application_id!)
                                    }
                                  >
                                    <span
                                      className={
                                        guest.retryingApplicationId === application.yardi_application_id
                                          ? 'invisible'
                                          : undefined
                                      }
                                      aria-hidden={
                                        guest.retryingApplicationId === application.yardi_application_id
                                      }
                                    >
                                      Retry
                                    </span>
                                    {guest.retryingApplicationId === application.yardi_application_id ? (
                                      <Loader2
                                        className="absolute h-4 w-4 animate-spin"
                                        aria-label="Retrying"
                                      />
                                    ) : null}
                                  </button>
                                ) : canInviteGuestCardApplication(application) ? (
                                  <button
                                    type="button"
                                    onClick={() => guestInvite.openInviteModal(application)}
                                    data-testid={`yardi-guest-search-view-button-${rowSuffix}`}
                                    className={cn(
                                      yardiTableStyles.guestSearchViewButton,
                                      'relative inline-flex items-center justify-center rounded-lg border-0 bg-[rgb(21_151_255)] px-3 py-1.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all',
                                      'hover:bg-[#0d7acc] hover:shadow-xl',
                                    )}
                                  >
                                    Invite
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr data-testid={`yardi-guest-search-table-row-detail-${rowSuffix}`}>
                                <td
                                  colSpan={9}
                                  data-testid={`yardi-guest-search-table-applicant-detail-cell-${rowSuffix}`}
                                >
                                  <YardiGuestCardOccupantsPanel
                                    applicants={application.applicants}
                                    rowSuffix={rowSuffix}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div
              data-testid="yardi-guest-search-mobile-layout"
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {guest.applications.map((application, idx) => {
                const { expandKey, rowSuffix } = renderApplicationSummary(application, idx)
                const isExpanded = expandedApplicationKeys.has(expandKey)

                return (
                  <div
                    key={expandKey}
                    data-testid={`yardi-guest-search-mobile-card-${rowSuffix}`}
                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-500">Unit Number</p>
                        <p className="break-words text-base font-semibold text-slate-900">
                          {application.unitNumber || '—'}
                        </p>
                      </div>
                      <button
                        type="button"
                        className={cn(
                          'shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700',
                          'hover:bg-slate-100',
                        )}
                        data-testid={`yardi-guest-search-mobile-expand-${rowSuffix}`}
                        onClick={() => toggleApplication(expandKey)}
                        aria-expanded={isExpanded}
                        aria-label="Toggle applicants"
                      >
                        <span className="inline-flex items-center gap-1">
                          {isExpanded ? (
                            <>
                              Hide
                              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                            </>
                          ) : (
                            <>
                              Occupants
                              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                            </>
                          )}
                        </span>
                      </button>
                    </div>
                    <GridRow
                      label="Lease Start Date"
                      value={formatShortDate(application.leaseStartDate)}
                      fieldTestId={`yardi-guest-search-mobile-field-lease-start-${rowSuffix}`}
                    />
                    <GridRow
                      label="Target Move In Date"
                      value={formatShortDate(application.targetMoveInDate)}
                      fieldTestId={`yardi-guest-search-mobile-field-target-move-in-${rowSuffix}`}
                    />
                    <GridRow
                      label="Expected Move In Date"
                      value={formatShortDate(application.expectedMoveInDate)}
                      fieldTestId={`yardi-guest-search-mobile-field-expected-move-in-${rowSuffix}`}
                    />
                    <GridRow
                      label="Credit Check Status"
                      value={applicationCreditCheckStatus(application)}
                      fieldTestId={`yardi-guest-search-mobile-field-credit-check-${rowSuffix}`}
                    />
                    <GridRow
                      label="Lease Duration"
                      value={application.leaseDuration}
                      fieldTestId={`yardi-guest-search-mobile-field-lease-duration-${rowSuffix}`}
                    />
                    <GridRow
                      label="Status"
                      value={
                        application.state
                          ? YARDI_STATUS_MAPPING[application.state] || application.state
                          : ''
                      }
                      fieldTestId={`yardi-guest-search-mobile-field-status-${rowSuffix}`}
                    />
                    {application.state === 'FAILED' && application.lr_check_error != null && (
                      <div
                        className="min-w-0"
                        data-testid={`yardi-guest-search-mobile-field-failure-reason-${rowSuffix}`}
                      >
                        <p className="mt-1.5 text-xs font-semibold text-slate-500">Application Failure Reason</p>
                        <p className="break-words text-sm">
                          {formatLrCheckErrorForDisplay(application.lr_check_error)}
                        </p>
                      </div>
                    )}
                    {canRetryGuestCardApplication(application) ? (
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          data-testid={`yardi-guest-search-mobile-retry-button-${rowSuffix}`}
                          disabled={guest.retryingApplicationId != null}
                          className={cn(
                            'relative inline-flex cursor-pointer items-center justify-center rounded-lg border-0 bg-[rgb(21_151_255)] px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90',
                            'disabled:pointer-events-none disabled:opacity-50',
                          )}
                          onClick={() =>
                            void guest.retryapplication(application.yardi_application_id!)
                          }
                        >
                          <span
                            className={
                              guest.retryingApplicationId === application.yardi_application_id
                                ? 'invisible'
                                : undefined
                            }
                            aria-hidden={
                              guest.retryingApplicationId === application.yardi_application_id
                            }
                          >
                            Retry
                          </span>
                          {guest.retryingApplicationId === application.yardi_application_id ? (
                            <Loader2
                              className="absolute h-4 w-4 animate-spin"
                              aria-label="Retrying"
                            />
                          ) : null}
                        </button>
                      </div>
                    ) : canInviteGuestCardApplication(application) ? (
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => guestInvite.openInviteModal(application)}
                          data-testid={`yardi-guest-search-mobile-view-button-${rowSuffix}`}
                          className="inline-flex cursor-pointer items-center justify-center rounded-lg border-0 bg-[rgb(21_151_255)] px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90"
                        >
                          Invite
                        </button>
                      </div>
                    ) : null}
                    {isExpanded && (
                      <div className="mt-3" data-testid={`yardi-guest-search-mobile-detail-${rowSuffix}`}>
                        <YardiGuestCardOccupantsPanel
                          applicants={application.applicants}
                          rowSuffix={rowSuffix}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        ) : (
          <TableSearchFilterEmptyPanel
            data-testid={
              isListView ? 'yardi-guest-search-table-empty-message' : 'yardi-guest-search-mobile-empty'
            }
          />
        )}
      </div>
    </div>
  )
}

function GridRow({
  label,
  value,
  fieldTestId,
}: GridRowProps) {
  return (
    <div className="mb-1.5" data-testid={fieldTestId}>
      <p className="text-xs font-semibold leading-tight text-slate-500">{label}</p>
      <p className="text-sm leading-snug text-slate-800">{value ?? ''}</p>
    </div>
  )
}
